import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users, GraduationCap, Rocket, Code, Brain, Sparkles,
  Cloud, GitBranch, Cpu, CircuitBoard, Settings2, Bug, Smartphone,
  CheckCircle2, Clock, Award, MapPin, Mail, Phone,
  ArrowRight, Monitor, Wifi, Layers, Check,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

const domains = [
  { icon: Bug,          name: 'Software Testing',              color: 'bg-rose-500',    topics: ['Manual & Automation Testing', 'Selenium', 'API Testing', 'JIRA'] },
  { icon: Smartphone,   name: 'Mobile App Development',        color: 'bg-purple-500',  topics: ['Android (Java/Kotlin)', 'iOS (Swift)', 'Flutter', 'React Native'] },
  { icon: Code,         name: 'Full Stack Web Development',    color: 'bg-blue-500',    topics: ['React.js', 'Node.js', 'JavaScript', 'MongoDB', 'MySQL'] },
  { icon: Brain,        name: 'AI & Machine Learning',         color: 'bg-teal-500',    topics: ['Python', 'Scikit-learn', 'TensorFlow', 'Model Building & Deployment'] },
  { icon: Sparkles,     name: 'Generative AI (GenAI)',         color: 'bg-violet-500',  topics: ['LLMs', 'Prompt Engineering', 'LangChain', 'RAG', 'AI App Dev'] },
  { icon: Cloud,        name: 'Cloud Computing',               color: 'bg-sky-500',     topics: ['AWS', 'Azure', 'GCP', 'Architecture & Deployment'] },
  { icon: GitBranch,    name: 'DevOps & MLOps',               color: 'bg-orange-500',  topics: ['Docker', 'Kubernetes', 'CI/CD', 'Jenkins', 'Git'] },
  { icon: Cpu,          name: 'IoT & Robotics',               color: 'bg-green-500',   topics: ['Raspberry Pi', 'Arduino', 'Sensor Integration', 'Automation'] },
  { icon: CircuitBoard, name: 'Embedded Systems',             color: 'bg-amber-500',   topics: ['C/C++', 'Microcontrollers', 'RTOS', 'Hardware Interfacing'] },
  { icon: Settings2,    name: 'PLC (Programmable Logic)',     color: 'bg-indigo-500',  topics: ['Industrial Automation', 'Ladder Logic', 'SCADA Integration'] },
];

const programTypes = [
  { icon: Code,    title: 'Technical Workshops', duration: '1–3 Days', students: '50–200', color: 'bg-blue-500',   desc: 'Single domain deep-dive with tool-specific hands-on sessions. Ideal for tech fests, orientation programs, and awareness workshops.' },
  { icon: Rocket,  title: 'Bootcamps',           duration: '1–2 Weeks', students: '30–100', color: 'bg-purple-500', desc: 'Intensive training covering fundamentals to intermediate level with mini-projects and assessments.' },
  { icon: Users,   title: 'Technical Talks',     duration: '2–4 Hours', students: '100–500', color: 'bg-green-500', desc: 'Inspiring sessions on emerging technologies, career guidance, industry insights, and startup ecosystem.' },
  { icon: Brain,   title: 'Hackathons',          duration: '1–2 Days', students: '50–200', color: 'bg-orange-500', desc: 'Competitive events where students build innovative solutions to real-world problems with expert mentoring.' },
];

const deliveryModes = [
  {
    icon: MapPin, label: 'In-Campus (Offline)', color: 'bg-blue-500/15 text-blue-400',
    points: ['Expert trainers deployed directly at your campus', 'Hands-on lab sessions & live demonstrations', 'Direct mentorship with real-time feedback', 'Ideal for structured, immersive technical upskilling'],
  },
  {
    icon: Wifi, label: 'Online / Remote', color: 'bg-purple-500/15 text-purple-400',
    points: ['Live instructor-led sessions via video conferencing', 'Recorded sessions for revision & self-paced learning', 'Regular assessments & project submissions', 'Suitable for institutions spread across geographies'],
  },
  {
    icon: Layers, label: 'Hybrid (Recommended)', color: 'bg-orange-500/15 text-orange-400',
    badge: 'Recommended',
    points: ['In-campus foundation + extended online delivery', 'Core concepts in-person, advanced topics online', 'Proven: Executed at Acharya Polytechnic (1 mo. campus + 2 mo. online)', 'Maximises depth while accommodating schedules'],
  },
];

const durations = [
  { days: '7 Days',   type: 'Crash Course / Workshop',       modes: 'In-Campus / Online',       highlight: 'Single domain deep-dive; tool/tech-specific sessions; ideal for fests & orientation.' },
  { days: '15 Days',  type: 'Short-Term Intensive',          modes: 'In-Campus / Online / Hybrid', highlight: 'Fundamentals to intermediate; mini-projects & assessments; semester breaks.' },
  { days: '1 Month',  type: 'Certificate Program',           modes: 'In-Campus / Online / Hybrid', highlight: 'End-to-end domain training with hands-on projects; theory + practice balance.' },
  { days: '3 Months', type: 'Comprehensive Diploma Program', modes: 'Hybrid / Online',            highlight: 'Full-cycle: basics to advanced; capstone projects; placement preparation.', recommended: true },
];

const engagements = [
  {
    institution: 'Acharya Polytechnic College',
    location: 'Bangalore, Karnataka',
    duration: '3 Months', mode: 'Hybrid',
    desc: '1 month intensive in-campus + 2 months online. Multiple domains including Software Testing, Web Development. Students completed hands-on projects evaluated as part of the curriculum.',
    accent: 'border-blue-500/30',
  },
  {
    institution: 'Sri Eswar College of Engineering',
    location: 'Tamil Nadu',
    duration: '16 Days', mode: 'In-Campus',
    desc: "Focused technical bootcamp for engineering students. Intensive on-site training delivered by CALLOC's subject matter experts with hands-on practical exposure.",
    accent: 'border-purple-500/30',
  },
  {
    institution: 'TN Skills · Karnataka Skills · India Skills',
    location: 'Pan India',
    duration: 'Ongoing', mode: 'Collaborative / Bootcamp',
    desc: 'CALLOC is an empanelled training member with Ethnus Consultancy Services for government-backed skill missions. Domains: Mobile App Development & Software Testing.',
    accent: 'border-orange-500/30',
    badge: 'National Level',
  },
];

const whyUs = [
  { icon: CheckCircle2, title: 'Industry-Aligned Curriculum',           desc: 'Content developed and regularly updated by practicing software engineers with direct industry experience.' },
  { icon: Rocket,       title: 'Project-Based Learning',               desc: 'Every program includes capstone and mini-projects mirroring real-world software development scenarios.' },
  { icon: Layers,       title: 'Flexible Engagement Models',           desc: '2-day workshop, 2-week bootcamp, or 3-month hybrid — CALLOC designs and delivers accordingly.' },
  { icon: Award,        title: 'Proven Track Record',                  desc: 'Engagements with Acharya Polytechnic, Sri Eswar College, and national skill programs demonstrate quality.' },
  { icon: GitBranch,    title: 'National Skill Development Tie-up',    desc: 'Active collaboration with Ethnus Consultancy Services for TN Skills, Karnataka Skills & India Skills.' },
  { icon: Users,        title: 'Dedicated Post-Training Support',      desc: 'Doubt-clearing, project mentorship, and placement-oriented guidance offered as value-adds.' },
];

export default function Campus2Training() {
  const [formData, setFormData] = useState({
    institutionName: '', contactPerson: '', email: '', phone: '',
    studentCount: '', programType: '', domain: '', duration: '', preferredDate: '', message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = `Institution: ${formData.institutionName}%0D%0AContact: ${formData.contactPerson}%0D%0AEmail: ${formData.email}%0D%0APhone: ${formData.phone}%0D%0AStudents: ${formData.studentCount}%0D%0AProgram: ${formData.programType}%0D%0ADomain: ${formData.domain}%0D%0ADuration: ${formData.duration}%0D%0ADate: ${formData.preferredDate}%0D%0A%0D%0AMessage: ${formData.message}`;
    window.location.href = `mailto:officecalloc@gmail.com?subject=Campus2Training Request - ${formData.institutionName}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-[#060D1F]">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-20 bg-[#060D1F] overflow-hidden">
        <div className="absolute top-10 right-10 w-96 h-96 bg-orange-500/15 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-10 w-72 h-72 bg-blue-600/15 rounded-full blur-[80px]" />
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '36px 36px' }} />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 pt-20 pb-24 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.span variants={fadeUp}
              className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <GraduationCap className="w-4 h-4" /> Campus Outreach · Technical Training · Skill Development
            </motion.span>
            <motion.h1 variants={fadeUp} className="text-5xl lg:text-7xl font-bold text-white mb-5 leading-tight">
              Campus<span className="text-orange-500">2</span>Training
            </motion.h1>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg max-w-3xl mx-auto mb-10">
              CALLOC brings industry-grade technical training directly to your campus — workshops, bootcamps, hackathons, and certification programs across 10 high-demand technology domains.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-10">
              {[{ val: '500+', label: 'Students Trained' }, { val: '50+', label: 'Colleges' }, { val: '10', label: 'Tech Domains' }, { val: '4.8/5', label: 'Rating' }].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-3xl font-bold text-white">{s.val}</div>
                  <div className="text-slate-500 text-sm mt-1">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* TRAINING DOMAINS */}
      <section className="py-20 bg-[#080F1E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">10 Domains</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mt-3">Training <span className="text-orange-500">Domains & Expertise</span></motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3 max-w-2xl mx-auto">
              Structured programs combining theoretical foundations with hands-on practice, real-world case studies, and capstone projects.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {domains.map((d) => {
              const Icon = d.icon;
              return (
                <motion.div key={d.name} variants={fadeUp}
                  whileHover={{ y: -5, boxShadow: '0 16px 40px rgba(0,0,0,0.4)' }}
                  className="group bg-[#0C1628] border border-white/10 rounded-2xl p-5 hover:border-orange-500/40 transition-all duration-300">
                  <div className={`w-10 h-10 ${d.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-bold text-white text-sm mb-2 group-hover:text-orange-400 transition-colors leading-snug">{d.name}</h3>
                  <div className="space-y-1">
                    {d.topics.slice(0, 3).map((t) => (
                      <div key={t} className="flex items-center gap-1.5 text-xs text-slate-400">
                        <span className="w-1 h-1 bg-orange-400 rounded-full flex-shrink-0" />{t}
                      </div>
                    ))}
                    {d.topics.length > 3 && <div className="text-xs text-orange-400 font-medium">+{d.topics.length - 3} more</div>}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* PROGRAM TYPES */}
      <section className="py-20 bg-[#060D1F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">What We Offer</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mt-3">Program <span className="text-orange-500">Types</span></motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {programTypes.map((p) => {
              const Icon = p.icon;
              return (
                <motion.div key={p.title} variants={fadeUp} whileHover={{ y: -6 }}
                  className="bg-[#0C1628] border border-white/10 rounded-2xl p-6 hover:border-orange-500/40 transition-all">
                  <div className={`w-12 h-12 ${p.color} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{p.title}</h3>
                  <div className="flex gap-3 text-xs text-slate-500 mb-3">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.duration}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {p.students}</span>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* DELIVERY MODES */}
      <section className="py-20 bg-[#080F1E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Delivery Flexibility</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mt-3">Training <span className="text-orange-500">Delivery Modes</span></motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3 max-w-xl mx-auto">Every program is customisable in content, depth, and delivery format.</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
            {deliveryModes.map((m) => {
              const Icon = m.icon;
              return (
                <motion.div key={m.label} variants={fadeUp}
                  className={`relative bg-[#0C1628] border-2 rounded-2xl p-7 ${m.badge ? 'border-orange-500/50 shadow-orange-500/10 shadow-xl' : 'border-white/10'}`}>
                  {m.badge && (
                    <span className="absolute -top-3 left-6 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">{m.badge}</span>
                  )}
                  <div className={`w-12 h-12 ${m.color} rounded-xl flex items-center justify-center mb-5`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-4">{m.label}</h3>
                  <ul className="space-y-2.5">
                    {m.points.map((pt) => (
                      <li key={pt} className="flex gap-2 items-start text-sm text-slate-400">
                        <Check className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />{pt}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* DURATION OPTIONS */}
      <section className="py-20 bg-[#060D1F] relative overflow-hidden">
        <div className="absolute top-10 right-10 w-80 h-80 bg-orange-500/10 rounded-full blur-[80px]" />
        <div className="absolute bottom-10 left-10 w-60 h-60 bg-blue-600/10 rounded-full blur-[60px]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-400 font-semibold text-sm uppercase tracking-widest">Program Durations</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mt-3">Duration <span className="text-orange-500">Options</span></motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3 max-w-xl mx-auto">Four standard durations — each delivering meaningful outcomes. Custom durations available on request.</motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {durations.map((d) => (
              <motion.div key={d.days} variants={fadeUp}
                className={`relative rounded-2xl p-6 border transition-all ${d.recommended ? 'bg-orange-500 border-orange-400 shadow-xl shadow-orange-500/20' : 'bg-white/5 border-white/10'}`}>
                {d.recommended && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white text-orange-500 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Most Popular</span>
                )}
                <div className={`text-3xl font-bold mb-1 ${d.recommended ? 'text-white' : 'text-orange-400'}`}>{d.days}</div>
                <div className={`font-semibold text-sm mb-3 ${d.recommended ? 'text-white/90' : 'text-white'}`}>{d.type}</div>
                <div className={`text-xs mb-4 ${d.recommended ? 'text-white/70' : 'text-slate-400'}`}>
                  <span className="font-medium">Modes:</span> {d.modes}
                </div>
                <p className={`text-sm leading-relaxed ${d.recommended ? 'text-white/80' : 'text-slate-400'}`}>{d.highlight}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* INSTITUTIONAL ENGAGEMENTS */}
      <section className="py-20 bg-[#080F1E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Our Clients</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mt-3">Key Institutional <span className="text-orange-500">Engagements</span></motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3 max-w-xl mx-auto">Training delivered to premier educational institutions and national skill development bodies.</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
            {engagements.map((e) => (
              <motion.div key={e.institution} variants={fadeUp} whileHover={{ y: -4 }}
                className={`relative bg-[#0C1628] border-2 ${e.accent} rounded-2xl p-7`}>
                {e.badge && (
                  <span className="absolute top-5 right-5 bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-lg">{e.badge}</span>
                )}
                <h3 className="font-bold text-white text-lg mb-2 pr-12">{e.institution}</h3>
                <div className="flex gap-1 items-center text-sm text-slate-400 mb-1">
                  <MapPin className="w-3.5 h-3.5" /> {e.location}
                </div>
                <div className="flex gap-4 text-sm text-slate-400 mb-4">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {e.duration}</span>
                  <span className="flex items-center gap-1"><Monitor className="w-3.5 h-3.5" /> {e.mode}</span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">{e.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* WHY PARTNER */}
      <section className="py-20 bg-[#060D1F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Why Choose Us</motion.span>
            <motion.h2 variants={fadeUp} className="text-4xl font-bold text-white mt-3">Why Partner with <span className="text-orange-500">CALLOC?</span></motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyUs.map((w) => {
              const Icon = w.icon;
              return (
                <motion.div key={w.title} variants={fadeUp} whileHover={{ x: 4 }}
                  className="flex gap-4 items-start p-5 bg-[#0C1628] border border-white/10 rounded-2xl hover:border-orange-500/30 hover:bg-orange-500/5 transition-all">
                  <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-sm mb-1">{w.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{w.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* REQUEST FORM */}
      <section className="py-20 bg-[#080F1E]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-10">
              <span className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Get Started</span>
              <h2 className="text-4xl font-bold text-white mt-3">Request a <span className="text-orange-500">Program</span></h2>
              <p className="text-slate-400 mt-3">Tell us your requirements and we'll design a program tailored to your institution.</p>
            </motion.div>

            <motion.div variants={fadeUp} className="bg-[#0C1628] border border-white/10 rounded-3xl p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  {[
                    { label: 'Institution Name', key: 'institutionName', type: 'text', placeholder: 'College / University / Institute' },
                    { label: 'Contact Person', key: 'contactPerson', type: 'text', placeholder: 'Your name' },
                    { label: 'Email', key: 'email', type: 'email', placeholder: 'your@email.com' },
                    { label: 'Phone', key: 'phone', type: 'tel', placeholder: '+91 XXXXXXXXXX' },
                    { label: 'Expected Student Count', key: 'studentCount', type: 'number', placeholder: 'e.g. 100' },
                  ].map(({ label, key, type, placeholder }) => (
                    <div key={key}>
                      <label className="block text-sm font-semibold text-slate-300 mb-2">{label} *</label>
                      <input type={type} required value={formData[key]}
                        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                        placeholder={placeholder}
                        className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent" />
                    </div>
                  ))}
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Program Type *</label>
                    <select required value={formData.programType}
                      onChange={(e) => setFormData({ ...formData, programType: e.target.value })}
                      className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent">
                      <option value="">Select program type</option>
                      <option>Technical Workshop</option>
                      <option>Bootcamp</option>
                      <option>Technical Talk</option>
                      <option>Hackathon</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Training Domain</label>
                    <select value={formData.domain}
                      onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                      className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent">
                      <option value="">Select domain</option>
                      {domains.map((d) => <option key={d.name}>{d.name}</option>)}
                      <option>Multiple Domains</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Preferred Duration</label>
                    <select value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent">
                      <option value="">Select duration</option>
                      <option>7 Days – Crash Course</option>
                      <option>15 Days – Short-Term Intensive</option>
                      <option>1 Month – Certificate Program</option>
                      <option>3 Months – Comprehensive Diploma</option>
                      <option>Custom Duration</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Preferred Date</label>
                  <input type="date" value={formData.preferredDate}
                    onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent [color-scheme:dark]" />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Additional Message</label>
                  <textarea rows="4" value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Any specific requirements, topics, or questions..."
                    className="w-full px-4 py-3 bg-[#080F1E] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent resize-none" />
                </div>

                <motion.button type="submit" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors">
                  <Rocket className="w-5 h-5" /> Submit Request <ArrowRight className="w-4 h-4" />
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#060D1F]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h3 variants={fadeUp} className="text-3xl font-bold text-white mb-3">Ready to empower your students?</motion.h3>
            <motion.p variants={fadeUp} className="text-slate-400 mb-8">CALLOC Technologies welcomes collaboration proposals from colleges, universities, polytechnics, and skill development bodies.</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              <a href="mailto:officecalloc@gmail.com"
                className="flex items-center gap-2 bg-white/10 border border-white/20 text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-white/15 transition-colors">
                <Mail className="w-4 h-4 text-orange-400" /> officecalloc@gmail.com
              </a>
              <a href="tel:+918971504091"
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors">
                <Phone className="w-4 h-4" /> +91 8971504091
              </a>
            </motion.div>
            <motion.p variants={fadeUp} className="text-slate-500 text-xs mt-6 italic">
              "Empowering Learners. Enabling Careers. Engineering the Future." — CALLOC Technologies
            </motion.p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
